import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'react-styled-flexboxgrid';

import useFetchMyIncidents from './hooks/useFetchMyIncidents';
import IncidentList from './IncidentList';
import LogoutModal from './LogoutModal';
import MapModal from './MapModal';

import { Footer } from '../../App/Footer';
import { Avatar } from '../../Global/Atomics/Avatar';
import Box from '../../Global/Atomics/Box';
import { StyledButton } from '../../Global/Atomics/Button';
import { Container } from '../../Global/Atomics/Container';
import { Heading } from '../../Global/Atomics/Heading';
import LoadingNova from '../../Global/Atomics/LoadingNova';
import NovaCircle from '../../Global/Atomics/NovaCircle';
import { Paragraph } from '../../Global/Atomics/Paragraph';
import StickyFooter from '../../Global/Atomics/StickyFooter';
import { Tab, Tabs } from '../../Global/Atomics/Tabs';
import CustomIcon from '../../Global/Icons/CustomIcon';

import { TSerializedIncident } from '../../../api/types';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useGlobalStateContext } from '../../../contexts/GlobalStateContext';
import { useResponsiveContext } from '../../../contexts/ResponsiveContext';
import { useTranslationContext } from '../../../contexts/TranslationContext';

const Profile = () => {
    const navigate = useNavigate();
    const navRef = useRef<HTMLElement>(null);
    const [showModal, setShowModal] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const { globalState: { tab }, setGlobalState } = useGlobalStateContext();
    const { user } = useAuthContext();
    const { resources } = useTranslationContext();
    const [viewMode] = useResponsiveContext();
    const { data, isLoading, isError } = useFetchMyIncidents({ tab });

    const isMobile = viewMode === 'mobile';
    let size = isMobile ? 158 : 220;

    const setTab = (num: number) => setGlobalState({ id: 'tab', value: num });

    // Update tab scroll position when user switches tabs
    navRef.current?.scrollTo({
        top: 0,
        left: navRef.current?.offsetWidth * (tab === 0 ? 0 : tab === 1 ? 0.1 : tab === 2 ? 0.3 : 0.5),
        behavior: 'smooth'
    });

    return (
        <>
            <Box position="relative" bg="green-nova" height={isMobile ? "40vh" : "35vh"} pt="lg">
                <Container>
                    <NovaCircle size={size} customPosition="top-right" zIndex={1} />
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb="xl">
                        <Box display="flex">
                            <StyledButton onClick={() => { navigate('/home'); setGlobalState({ reset: true }) }} aria-label="Navigate home">
                                <CustomIcon icon={"ArrowLeft_sm"} customColor="white" />
                            </StyledButton>
                            <Avatar margin="0 0 0 8px" border="none" position="relative" bg="black-1" size={40} >
                                <Box as="span" typography='ui-xl-medium' color="white">
                                    {user?.name.split(" ")[0].charAt(0).toUpperCase()}{user?.name.split(" ").pop().charAt(0).toUpperCase()}
                                </Box>
                            </Avatar>
                        </Box>
                        <Box display="flex" alignItems="center" justifyContent="center" position="relative">
                            <StyledButton onClick={() => setShowLogoutModal(true)} aria-label="Logout">
                                <CustomIcon icon={"LogoutIcon"} customColor="white" />
                            </StyledButton>
                        </Box>
                    </Box>

                    {/* USERNAME, TITLE & NAVIGATION */}
                    <Row center="md">
                        <Col xs={12} md={10}>

                            <Box ml="sm" pl="xs">
                                {/* Username */}
                                <Heading level={3} typography="text-m-bold" mb="md" customColor="white">{user?.name?.split(" ")[0]} {user?.name.split(" ").pop()}</Heading>

                                {/* Title */}
                                <Box display="flex" mb="md">
                                    <Heading level={1} typography="text-xl-light" customColor="white">{resources.label.my}&nbsp;</Heading>
                                    <Heading level={1} typography="text-xl-bold" customColor="white">{resources.label.incidents}</Heading>
                                </Box>
                            </Box>

                            <Box mb="lg">
                                {/* Tabs */}
                                <Tabs active={tab ?? 0} bg="neutral1" stateId="tabId" setTab={setTab} ref={navRef}>
                                    <Tab tab={<Box typography='text-s-bold'>{resources.profile.tabs.open}</Box>}>
                                        <IncidentList data={data as TSerializedIncident[]} setTab={setTab} tab={tab as number} maxTab={4} isError={isError} />
                                    </Tab>
                                    <Tab tab={<Box typography='text-s-bold'>{resources.profile.tabs.underAnalysis}</Box>}>
                                        <IncidentList data={data as TSerializedIncident[]} setTab={setTab} tab={tab as number} maxTab={4} isError={isError} />
                                    </Tab>
                                    <Tab tab={<Box typography='text-s-bold'>{resources.profile.tabs.ongoingResolution}</Box>}>
                                        <IncidentList data={data as TSerializedIncident[]} setTab={setTab} tab={tab as number} maxTab={4} isError={isError} />
                                    </Tab>
                                    <Tab tab={<Box typography='text-s-bold'>{resources.profile.tabs.resolved}</Box>}>
                                        <IncidentList data={data as TSerializedIncident[]} setTab={setTab} tab={tab as number} maxTab={4} isError={isError} />
                                    </Tab>
                                </Tabs>
                            </Box>
                        </Col>
                    </Row>

                </Container>

            </Box>

            {/* STICKY FOOTER */}
            <StickyFooter>
                <Container>
                    <Row center="xs">
                        <Col xs={9} md={4}>
                            <Box display="flex" flexDirection="column" justifyContent="center" alignContent="center">
                                <Box display="flex" flexDirection='column' justifyContent="center" alignItems="center">
                                    <StyledButton onClick={() => setShowModal(true)} aria-label={resources.label.newReport}>
                                        <Avatar border="none" bg="error-4" size={56}>
                                            <CustomIcon icon="PlusNova" customColor='white' />
                                        </Avatar>
                                    </StyledButton>
                                    <Paragraph typography='text-xs-regular'>{resources.label.newReport}</Paragraph>
                                </Box>
                            </Box>
                        </Col>
                    </Row>
                </Container>

                {/* MAIN FOOTER */}
                <Footer pt="lg" />
            </StickyFooter>

            {/* MODAL MAP REPORT */}
            <MapModal showModal={showModal} setShowModal={setShowModal} />

            {/* MODAL LOGOUT */}
            <LogoutModal showModal={showLogoutModal} setShowModal={setShowLogoutModal} />

            {/* LOADING */}
            {isLoading && <LoadingNova />}
        </>
    );
};

export default Profile;